import {
  API_CALL,
  API_CALL_FAILURE,
  API_CALL_REQUEST,
  API_CALL_SUCCESS,
} from 'actions/actionConstants'
import type { AnyAction, Middleware } from 'redux'

export interface ApiAction<T> extends AnyAction {
  type: 'API_CALL'
  api: string
  request: () => Promise<T>
  data?: T
}

export type ApiActionRequest<F = {}> = {
  type: 'API_CALL_REQUEST'
  api: string
} & F

export type ApiActionSuccess<T, F = {}> = {
  type: 'API_CALL_SUCCESS'
  api: string
  data: T
} & F

export type ApiActionFailure<E, F = {}> = {
  type: 'API_CALL_FAILURE'
  api: string
  error: E
} & F

export interface ApiDispatch {
  <T extends ApiAction<any>>(apiAction: T): T
}
export type ApiMiddleware<S> = Middleware<ApiDispatch, S>

function isApiAction<T = unknown>(action: AnyAction): action is ApiAction<T> {
  return action.type === API_CALL
}

const devEnvironments = new Set<string | undefined>(['development', 'test'])
const apiMiddleware: Middleware = (args) => {
  const { dispatch } = args
  return next => (action: AnyAction) => {
    if (isApiAction(action)) {
      const { request, ...rest } = action
      const apiRequest = request()
      const requestedAt = new Date().toISOString()
      // edge had an issue where apiRequest wasn't an instance of Promise weirdly
      if (devEnvironments.has(process.env.NODE_ENV) && !(apiRequest instanceof Promise)) {
        throw new Error(`Actions of type ${API_CALL} - the request property must be a function that returns a Promise.`)
      }
      apiRequest.then((res) => {
        dispatch({
          ...rest,
          requestedAt,
          data: res,
          type: API_CALL_SUCCESS,
        })
      }, (error) => {
        if (devEnvironments.has(process.env.NODE_ENV)) {
          console.debug(`Error during api call: ${rest.api}.`, error)
        }

        // We don't want to fire 'error' actions on the server side as we would rather
        // the client retry on it's own instead of storing 'error' into the state.
        // this gives it a better chance to come back or show a meaningful message
        if (!IS_SSR) {
          dispatch({
            ...rest,
            requestedAt,
            error,
            type: API_CALL_FAILURE,
          })
        }
      })

      if (IS_SSR) {
        // We don't want to fire 'request' actions on the server side as we don't need to
        // store if we were "fetching" something. This prevents errors where the server side
        // starts fetching, then gets timed out for taking too long  and the `fetching: true`
        // is passed to the client
        return next({ ...rest })
      }

      return next({
        ...rest,
        requestedAt,
        type: API_CALL_REQUEST,
      })
    }

    return next(action)
  }
}

export default apiMiddleware
